import { createContext, useContext } from "react";
import { useParams } from "react-router";

import { ApiClientProvider } from "@/api/ApiClientProvider";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { parseAccessTokenPayload } from "@/helpers/jwt";

import { RawAccessTokenProvider } from "./AccessTokenProvider";
import { ConfigUserProvider } from "./config/ConfigProvider";
import { QuickReplyQueryScope } from "./QuickReplyQueryScope";
import { QuickReplyUserLoader } from "./QuickReplyUserLoader";

type QuickReplyContextValues = {
  entityId: string;
};

const QuickReplyContext = createContext<QuickReplyContextValues | null>(null);

type QuickReplyProviderProps = {
  children: React.ReactNode;
};

/** Validates quick-reply credentials and composes an isolated token/API/self scope, without SuperTokens. */
export const QuickReplyProvider = ({ children }: QuickReplyProviderProps): React.ReactNode => {
  const { token: rawToken } = useParams<{ token: string }>();

  const token = rawToken ? rawToken.replace("V2-", "") : "";

  const payload = parseAccessTokenPayload(token);
  // Token expiry is an external credential validity check at route entry.
  // oxlint-disable-next-line react-hooks/purity
  const now = Math.ceil(Date.now() / 1000);
  if (!payload || !Number.isFinite(payload.exp) || payload.exp <= now) {
    return <ErrorPage status={404} />;
  }
  const { entityId, projectId } = payload;
  if (typeof entityId !== "string" || !entityId.trim() || typeof projectId !== "string" || !projectId.trim()) {
    return <ErrorPage status={404} />;
  }

  return (
    <QuickReplyContext value={{ entityId }}>
      <QuickReplyQueryScope key={token}>
        <RawAccessTokenProvider token={token}>
          <ConfigUserProvider>
            <ApiClientProvider token={token} projectId={projectId}>
              <QuickReplyUserLoader {...{ projectId }}>{children}</QuickReplyUserLoader>
            </ApiClientProvider>
          </ConfigUserProvider>
        </RawAccessTokenProvider>
      </QuickReplyQueryScope>
    </QuickReplyContext>
  );
};

export const useQuickReplyManager = (): QuickReplyContextValues => {
  const context = useContext(QuickReplyContext);
  if (!context) {
    throw new Error("Quick reply provider is missing");
  }

  return context;
};
